/**
 * Pupil numbers, and the name to show next to them.
 *
 * The class lists the school hands out are already numbered — "12 Dupont Léa" —
 * and that number is part of the line that gets pasted or imported, so it ends
 * up inside the pupil's name. Printed next to a column headed "N°" it reads
 * twice, and it goes stale the moment a pupil arrives or leaves.
 *
 * So the number is not stored at all: it is derived from the alphabetical
 * order of the class, which is how the school numbers them in the first place,
 * and the name is displayed without it. Nothing is rewritten in the store —
 * a name typed with a number in it keeps it, it is simply not shown.
 */

/**
 * Leading position marker: "12", "12.", "12)", "12 -", "n°12".
 *
 * Anchored to a separator or the end of the digits so a name that genuinely
 * starts with a digit keeps it — there is no such French surname, but a
 * pasted line like "2e année" is not a pupil either and should not be
 * silently truncated into "année".
 */
const LEADING_NUMBER = /^\s*(?:n[°o]\s*)?\d{1,3}\s*[.)\-–—:]?\s+/i;

/** The pupil's name as it should appear: no list number in front of it. */
export function studentDisplayName(name: string): string {
  const stripped = name.replace(LEADING_NUMBER, '').trim();
  // A line that is nothing but a number is not a name; keep what we were given
  // rather than showing an empty pupil.
  return stripped || name.trim();
}

export interface RosterStudent {
  id: string;
  name: string;
}

/**
 * Class numbers, alphabetical by name — the same order a paper class list uses.
 *
 * Keyed by id rather than returned as a sorted list so a screen can keep its
 * own order (most recent observation first, say) and still show the number the
 * pupil has on paper.
 */
export function studentNumbers(students: readonly RosterStudent[]): Map<string, number> {
  const sorted = [...students].sort((a, b) =>
    studentDisplayName(a.name).localeCompare(studentDisplayName(b.name), 'fr', {
      sensitivity: 'base',
    }),
  );

  return new Map(sorted.map((student, index) => [student.id, index + 1]));
}
